/**
 * Web push on the client: register the service worker, ask permission,
 * subscribe with the server's VAPID key, and store the subscription
 * (POST /push/subscribe). iOS needs the app on the Home Screen first.
 */

import { getPushVapidKey, subscribePush, unsubscribePush, getPushSubscriptions } from './api';

function urlBase64ToUint8Array(base64) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(b64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export function pushSupported() {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export function isIos() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent || '');
}

export function isStandalone() {
  return window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator.standalone === true;
}

export async function pushStatus() {
  if (!pushSupported()) return { supported: false, permission: 'unsupported', subscribed: false, serverCount: 0 };
  let subscribed = false;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    subscribed = !!sub;
  } catch { /* ignore */ }
  let serverCount = 0;
  let configured = true;
  try {
    const res = await getPushSubscriptions();
    if (!res.error) { serverCount = res.count || 0; configured = res.configured !== false; }
  } catch { /* ignore */ }
  return { supported: true, permission: Notification.permission, subscribed, serverCount, configured };
}

export async function enablePush() {
  if (!pushSupported()) throw new Error('Notifications are not supported in this browser.');
  if (isIos() && !isStandalone()) {
    throw new Error('On iPhone, add the Navigator to your Home Screen first (Share, then Add to Home Screen), then turn notifications on from there.');
  }
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') throw new Error('Notifications were not allowed.');
  const reg = await navigator.serviceWorker.register('/sw.js');
  await navigator.serviceWorker.ready;
  const key = await getPushVapidKey();
  if (!key || key.error || !key.public_key) throw new Error('Notifications are not set up on the server yet.');
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(key.public_key) });
  }
  const res = await subscribePush(sub.toJSON());
  if (res.error) throw new Error(res.error);
  return true;
}

export async function disablePush() {
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    if (sub) {
      await unsubscribePush(sub.endpoint);
      await sub.unsubscribe();
    } else {
      await unsubscribePush(null);
    }
  } catch { /* ignore */ }
  return true;
}
